import { isNil } from "lodash";
import { useState } from "react";

import { LockIcon } from "@8x8/oxygen-icon";
import TextArea from "@8x8/oxygen-textarea";

import DescriptionList from "@/react/components/DescriptionList";
import Input from "@/react/components/Input";
import { useErrorNotification } from "@/react/notifications";
import PageLayout from "@/react/features/styled/PageLayout";
import PageTitle from "@/react/components/PageTitle";

import { useLazyEncryptTextQuery } from "./api";
import {
  ComparisonArea,
  CopyTextButton,
  MainContainer,
  SubmitButton,
  TextAreaWrapper,
} from "./styled";

const ROWS = 25;
const MAX_INPUT_LENGTH = 10000;

const DESCRIPTION_INFO = Object.freeze([
  {
    term: "About:",
    description:
      "Encrypt Secrets tool is for the developers of maestro integration service to encrypt any client or customer secrets.",
  },
  {
    term: "Usage:",
    description:
      "Please copy and paste any plain text secret or password here on the Encrypt Secrets tool and encrypt it.\n" +
      "Once done encrypting, please replace the plain text secret with the encrypted cipher on the maestro configuration file (Pub8/IPS/Studio8).\n" +
      "You may also enter text directly into the box on the left, using Shift+Enter to insert newlines.\n" +
      "When the encrypted text is highlighted in orange, it indicates that the text on the left has been modified since the last encryption request.",
  },
]);

const EncryptSecretsPage = () => {
  const [inputText, setInputText] = useState("");
  const [inputError, setInputError] = useState(false);

  const [encryptText, encryptionResult] = useLazyEncryptTextQuery();
  const {
    data: encryptionResponse,
    isLoading,
    isFetching,
    error,
  } = encryptionResult;

  useErrorNotification(error);

  const onEncrypt = () => {
    if (inputText.length > 0 && inputText.length <= MAX_INPUT_LENGTH) {
      encryptText(inputText);
    }
  };

  const textLengthValidator = (event) => {
    const invalid = event.target.value.length > MAX_INPUT_LENGTH;
    setInputError(invalid);
    return !invalid;
  };

  const disabled =
    inputError || !inputText || inputText === "" || isLoading || isFetching;

  const plainText = encryptionResponse?.plainText ?? "";
  const syncStateBorder =
    !isNil(encryptionResponse) && plainText !== inputText
      ? "solid orange 2px"
      : undefined;
  const encryptedText = encryptionResponse?.cipherText ?? "";

  return (
    <PageLayout>
      <PageTitle>Encrypt Secrets</PageTitle>
      <MainContainer>
        <DescriptionList data={DESCRIPTION_INFO} />
        <ComparisonArea>
          <TextAreaWrapper>
            <Input
              placeholder="Enter secret to encrypt..."
              value={inputText}
              setValue={setInputText}
              onSubmit={onEncrypt}
              validator={textLengthValidator}
              hasError={inputError}
              multiLine
              rows={ROWS}
              submitOnEnter
              // Even if the input is invalid, we still want to allow the user to enter it
              // But we'll prevent the encryption from being requested
              allowInvalidInput
            />
          </TextAreaWrapper>
          <SubmitButton
            size="large"
            variant="primary"
            isDisabled={disabled}
            onClick={onEncrypt}
            isCircular
          >
            <LockIcon size={20} disabled={disabled} invertColors />
          </SubmitButton>
          <TextAreaWrapper>
            <TextArea
              style={{ border: syncStateBorder }}
              rows={ROWS}
              value={encryptedText}
            />
            <CopyTextButton value={encryptedText} />
          </TextAreaWrapper>
        </ComparisonArea>
        {inputError && <p>Input has limit of {MAX_INPUT_LENGTH} characters</p>}
      </MainContainer>
    </PageLayout>
  );
};

export default EncryptSecretsPage;
